<script setup lang="ts">
import { formatDate } from "@core/helpers"
import { Freq } from "@types"
import moment from "moment"

type Props = {
    message: string
    period: {
        freq: Freq
        date_from?: string
        date_to?: string
    }
}

const props = withDefaults(defineProps<Props>(), {
    message: "Aucune donnée disponible pour le moment.",
    period: undefined,
})

const emit = defineEmits(["click:dates"])

function computedDaysBetween(date_from?: string, date_to?: string): number {
    if (!date_from || !date_to) return 0
    // Utilise moment.js pour calculer le nombre de jours entre deux dates
    // Assurez-vous que moment est importé : import moment from "moment"
    const from = moment(date_from)
    const to = moment(date_to)
    return to.diff(from, "days")
}

function onClickDates() {
    if (
        props.period.freq === "raw" &&
        computedDaysBetween(props.period.date_from, props.period.date_to) > 365
    ) {
        emit("click:dates", {
            date_from: moment(props.period.date_to)
                .subtract(1, "year")
                .format("YYYY-MM-DD"),
            date_to: moment(props.period.date_to).format("YYYY-MM-DD"),
        })
    } else {
        emit("click:dates", {
            date_from: moment(props.period.date_from).format("YYYY-MM-DD"),
            date_to: moment(props.period.date_to).format("YYYY-MM-DD"),
        })
    }
}
</script>

<template>
    <DataChartMessage
        class="data-chart-message-empty-data"
        :message="message!"
        icon="pi pi-info-circle"
    >
        <p class="font-semibold">{{ message }}<br /></p>

        <p class="text-sm">
            La période sélectionnée ne contient pas de données.<br />
            <template v-if="period?.date_from && period?.date_to">
                Période de disponibilité des données est entre le
                {{ formatDate(period.date_from) }} au
                {{ formatDate(period.date_to) }}
            </template>
            <template v-else-if="period?.date_from">
                Donnée disponible à partir du
                {{ formatDate(period.date_from) }}
            </template>
            <template v-else-if="period?.date_to">
                Donnée disponibile jusqu'au
                {{ formatDate(period.date_to) }}
            </template>
            <template v-else> Dates non disponibles </template>
        </p>

        <p class="text-sm" v-if="period?.date_from && period?.date_to">
            Voulez-vous changer la période pour voir les données ?
        </p>
        <p
            class="text-xs text-gray-500"
            v-if="
                computedDaysBetween(period?.date_from, period?.date_to) > 200 &&
                period?.freq === 'raw' &&
                period?.date_from &&
                period?.date_to
            "
        >
            Attention, cette période couvre plus de 1 an. Si vous cliquer sur
            "Changer de période", le graphique affichera la dernière année de
            données disponibles.
        </p>
        <BaseButton
            v-if="period?.date_from && period?.date_to"
            class="max-w-fit mx-auto mt-4"
            label="Changer de période"
            size="small"
            @click.prevent.stop="onClickDates"
        ></BaseButton>
    </DataChartMessage>
</template>
